import { Link } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { getPosts, formatDate, type GhostPost } from '../lib/ghost'
import {
  ArrowRight,
  Sparkle,
  ChartLine,
  Robot,
  ShieldCheck,
  Lightning,
  Confetti,
  Question,
  Check,
  Briefcase,
} from '@phosphor-icons/react'
import { useAuth } from '../auth'
import { analytics, consentStatus } from '../lib/analytics'
import { HERO_SEED, HOMEPAGE_EXAMPLE } from '../lib/explainerSeeds'
import { getPublicUsage } from '../api'
import HeroFutures from '../components/HeroFutures'

// Hero copy A/B (docs/growth/positioning.md): 'odds' is the current
// outcome-first hero, 'assistant' frames LifeOdds as the decision assistant
// that runs the numbers. The assignment is persisted only once analytics
// consent is granted — before that each load draws fresh, and no events fire
// anyway. <title>/meta stay identical across arms so SEO isn't part of the test.
type HeroVariant = 'odds' | 'assistant'
const HERO_VARIANT_KEY = 'lo-hero-v'

function pickHeroVariant(): HeroVariant {
  try {
    const stored = localStorage.getItem(HERO_VARIANT_KEY)
    if (stored === 'odds' || stored === 'assistant') return stored
  } catch { /* storage blocked — fall through to a fresh draw */ }
  const v: HeroVariant = Math.random() < 0.5 ? 'odds' : 'assistant'
  if (consentStatus() === 'granted') {
    try { localStorage.setItem(HERO_VARIANT_KEY, v) } catch { /* ignore */ }
  }
  return v
}

export default function Landing() {
  const { user } = useAuth()
  // Anonymous-first funnel (signup-wall audit D1): the primary CTA goes
  // straight into the app — templates, warm start, and simulation all work
  // without an account, and the wall shows up naturally at save/AI. Signup
  // stays one click away as the secondary affordance.
  const ctaTarget = '/app'
  const ctaLabel = user ? 'Open the app' : 'Start free — no signup'
  const [heroVariant] = useState(pickHeroVariant)
  useEffect(() => {
    analytics.track('landing_hero_view', { variant: heroVariant, signed_in: !!user })
    // Once per mount — re-firing on auth hydration would double-count views.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroVariant])
  const heroRef = useRef<HTMLElement>(null)
  const heroCardRef = useRef<HTMLAnchorElement>(null)
  const waffleRef = useRef<HTMLCanvasElement>(null)
  const futuresCountRef = useRef<HTMLSpanElement>(null)
  const trackCta = (cta: 'primary' | 'signup') =>
    analytics.track('landing_cta_click', { variant: heroVariant, cta, signed_in: !!user })

  return (
    <>
    <Helmet>
      <title>LifeOdds — Find out which choice actually wins</title>
      <meta name="description" content="Describe your options. Model the uncertainty. 1,000 simulated futures vote on your best move. Free forever." />
      <link rel="canonical" href="https://lifeodds.app/" />
      <meta property="og:url" content="https://lifeodds.app/" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="LifeOdds — Find out which choice actually wins" />
      <meta property="og:description" content="Describe your options. Model the uncertainty. 1,000 simulated futures vote on your best move. Free forever." />
      <meta property="og:image" content="https://lifeodds.app/og-sample.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="LifeOdds — Find out which choice actually wins" />
      <meta name="twitter:image" content="https://lifeodds.app/og-sample.png" />
      <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
    </Helmet>
    <div className="space-y-24" data-testid="landing-page">
      {/* HERO */}
      <section ref={heroRef} className="anim-rise relative grid lg:grid-cols-12 gap-8 lg:gap-12 items-center pt-2">
        <HeroFutures
          heroRef={heroRef}
          cardRef={heroCardRef}
          waffleRef={waffleRef}
          counterRef={futuresCountRef}
          winPct={HOMEPAGE_EXAMPLE.heroResult.pct}
        />
        <div className="relative z-10 lg:col-span-7 space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-2 chip border-primary text-primary-ink">
              <Sparkle size={14} weight="fill" /> AI-assisted · built for humans, not analysts
            </div>
            <Link
              to="/how-it-works"
              data-testid="monte-carlo-clarifier"
              className="inline-flex items-center gap-1 chip border-edge bg-surface text-ink-muted hover:text-ink hover:bg-muted transition-colors"
              title="Monte Carlo simulation — not luck. Click to see the math."
            >
              <ChartLine size={12} weight="fill" /> Monte Carlo simulation, not luck →
            </Link>
          </div>
          {heroVariant === 'assistant' ? (
            <>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.92]">
                The decision assistant that actually{' '}
                <br className="hidden sm:block" />
                <span className="bg-primary text-white px-3 py-1 rounded-2xl inline-block -rotate-1 border-2 border-edge shadow-brutal-sm">
                  runs the numbers.
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-ink-muted max-w-xl font-medium">
                Tell it what you're weighing — a new job, a move, buy or rent. It drafts the model
                with you, runs <span className="mono font-bold text-ink">1,000 simulated futures</span>,
                and shows which option wins, how often, and what would flip the answer.
                Schedule a <span className="mono font-bold text-ink">check-in</span> and it asks you, on the
                date you pick, how it played out.
              </p>
            </>
          ) : (
            <>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.92]">
                Find out which choice actually wins —{' '}
                <br className="hidden sm:block" />
                <span className="bg-primary text-white px-3 py-1 rounded-2xl inline-block -rotate-1 border-2 border-edge shadow-brutal-sm">
                  before you make it.
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-ink-muted max-w-xl font-medium">
                Describe your options. Add your best guesses for salary, risk, cost. Hit run.{' '}
                <span className="mono font-bold text-ink">1,000</span> simulated futures
                vote on your best move. <span className="mono font-bold text-ink">No spreadsheets.</span>
              </p>
            </>
          )}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Link to={ctaTarget} data-testid="cta-primary" onClick={() => trackCta('primary')} className="btn-primary text-lg px-8 py-4 inline-flex items-center gap-2">
              {ctaLabel} <ArrowRight size={20} weight="bold" />
            </Link>
            {!user && (
              <Link to="/signup" data-testid="cta-secondary" onClick={() => trackCta('signup')} className="btn-secondary text-lg px-8 py-4">
                Create free account
              </Link>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-4 pt-3 text-sm text-ink-muted">
            <span className="inline-flex items-center gap-1"><Check size={16} weight="bold" className="text-accent-ink" /> Free forever for personal use</span>
            <span className="inline-flex items-center gap-1"><Check size={16} weight="bold" className="text-accent-ink" /> No credit card</span>
            <span className="inline-flex items-center gap-1"><Check size={16} weight="bold" className="text-accent-ink" /> Try without signup</span>
            <span className="inline-flex items-center gap-1"><Check size={16} weight="bold" className="text-accent-ink" /> Upgrade is one-time, not a subscription</span>
          </div>
        </div>

        {/* Visual */}
        <div className="relative z-10 lg:col-span-5">
          <div className="relative">
            <div className="absolute -top-4 -right-4 w-28 h-28 rounded-full bg-accent border-2 border-edge animate-float opacity-90" />
            <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-secondary border-2 border-edge opacity-80 animate-float" style={{ animationDelay: '2s' }} />
            {/* A real, sourced, reproducible engine run (verified by `npm run
                hero:check`) that opens in the builder so anyone can re-run it. */}
            <Link
              ref={heroCardRef}
              to={`/app?seed=${HOMEPAGE_EXAMPLE.key}`}
              data-testid="hero-example-card"
              className="block relative card-brutal p-6 bg-gradient-to-br from-primary to-primary-hover text-white"
            >
              <Confetti size={64} weight="duotone" className="absolute right-3 top-3 opacity-25" />
              <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-80 mb-2">
                Example run · {HOMEPAGE_EXAMPLE.h1} · {HOMEPAGE_EXAMPLE.horizonYears} years
              </div>
              {/* One square per future, in the example's real split; fills as
                  the background futures land (components/HeroFutures.tsx). */}
              <canvas
                ref={waffleRef}
                aria-hidden="true"
                className="block w-full max-w-full rounded-lg bg-black/10 mb-2"
                style={{ aspectRatio: '50 / 20' }}
              />
              <div className="flex justify-between gap-2 text-xs font-bold mono mb-4 opacity-90">
                <span>Future <span ref={futuresCountRef}>1,000</span> of 1,000</span>
                <span>■ = {HOMEPAGE_EXAMPLE.heroResult.winner} ahead</span>
              </div>
              <div className="font-heading text-3xl sm:text-4xl font-black mb-4">🏆 {HOMEPAGE_EXAMPLE.heroResult.winner}</div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                  <div className="text-xs opacity-80 font-bold">Wins</div>
                  <div className="font-heading text-2xl font-black mono">{HOMEPAGE_EXAMPLE.heroResult.pct}</div>
                </div>
                <div>
                  <div className="text-xs opacity-80 font-bold">Edge</div>
                  <div className="font-heading text-2xl font-black mono">{HOMEPAGE_EXAMPLE.heroResult.edge}</div>
                </div>
                <div>
                  <div className="text-xs opacity-80 font-bold">Ends below €0</div>
                  <div className="font-heading text-2xl font-black mono">{HOMEPAGE_EXAMPLE.heroResult.risk}</div>
                </div>
              </div>
              <div className="rounded-xl bg-white/15 border-2 border-white/30 backdrop-blur p-3 text-sm">
                A close call. Master's graduates start ~13% higher (StepStone graduate salary report), but
                two years without salary take most of a career to earn back. The answer hinges on that
                premium. 1,000 futures, seed {HERO_SEED}; each square above is one of them (the lines
                behind are illustrative). Open it and try your own numbers →
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Social proof bar */}
      <section className="anim-rise border-y-2 border-edge py-6 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 bg-muted/40" style={{ animationDelay: '60ms' }}>
        <div className="text-center text-xs font-bold uppercase tracking-[0.2em] text-ink-muted mb-3">
          What people model with LifeOdds
        </div>
        <UsageCount />
        <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-8 text-sm font-bold text-ink-muted">
          <span>💼 Job changes</span>
          <span>·</span>
          <span>🏙️ City moves</span>
          <span>·</span>
          <span>🎓 Education forks</span>
          <span>·</span>
          <span>🏠 Housing</span>
          <span>·</span>
          <span>💰 Investments</span>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="anim-rise space-y-10" style={{ animationDelay: '120ms' }}>
        <div className="max-w-2xl">
          <div className="chip border-secondary text-secondary-ink inline-flex mb-3">Features</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">
            Everything you need to decide with confidence.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <div key={i} className="card-brutal p-6" data-testid={`feature-${i}`}>
              <div className={`w-12 h-12 rounded-xl border-2 border-edge grid place-items-center mb-3 ${f.bg}`}>
                <f.Icon size={24} weight="duotone" className={f.color} />
              </div>
              <h3 className="font-heading text-xl font-bold mb-1">{f.title}</h3>
              <p className="text-sm text-ink-muted">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS (compact) */}
      <section className="anim-rise grid lg:grid-cols-2 gap-10 items-center" style={{ animationDelay: '180ms' }}>
        <div>
          <div className="chip border-primary text-primary-ink inline-flex mb-3">How it works</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-4">
            Three steps. <span className="text-primary-ink">No PhD required.</span>
          </h2>
          <ol className="space-y-4">
            {STEPS.map((s, i) => (
              <li key={i} className="flex gap-4">
                <div className="w-10 h-10 grid place-items-center rounded-xl bg-primary text-white font-heading font-black text-lg border-2 border-edge shadow-brutal-sm shrink-0">
                  {i + 1}
                </div>
                <div>
                  <div className="font-heading text-lg font-bold leading-tight">{s.title}</div>
                  <p className="text-sm text-ink-muted">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
          <Link to="/how-it-works" data-testid="how-detail-link" className="inline-flex items-center gap-1 mt-4 text-primary-ink font-bold underline underline-offset-2">
            Read the math <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
        <div className="card-brutal p-6 bg-gradient-to-br from-secondary/15 to-accent/15">
          <div className="label-bold mb-2">Ready-made templates</div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-3 rounded-xl border-2 border-edge bg-bg">
              <div className="text-xl">💼</div>
              <div>
                <div className="font-bold text-sm">"Should I take the new job?"</div>
                <div className="text-xs text-ink-muted mono">salary, raises, layoff risk</div>
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 rounded-xl border-2 border-edge bg-bg">
              <div className="text-xl">🏙️</div>
              <div>
                <div className="font-bold text-sm">"Move to a different city?"</div>
                <div className="text-xs text-ink-muted mono">7-year horizon</div>
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 rounded-xl border-2 border-edge bg-bg">
              <div className="text-xl">🎓</div>
              <div>
                <div className="font-bold text-sm">"Master's, bootcamp, or stay?"</div>
                <div className="text-xs text-ink-muted mono">10-year horizon</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY NOT A SPREADSHEET */}
      <section className="anim-rise space-y-6" style={{ animationDelay: '230ms' }}>
        <div className="text-center max-w-2xl mx-auto">
          <div className="chip border-secondary text-secondary-ink inline-flex mb-3">Why LifeOdds?</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">
            Why not just a spreadsheet?
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {COMPARISON.map((col, i) => (
            <div key={i} className={`card-brutal p-6 ${col.highlight ? 'bg-primary text-white border-primary' : ''}`}>
              <div className={`font-heading text-lg font-black mb-4 ${col.highlight ? 'text-white' : 'text-ink-muted'}`}>
                {col.label}
              </div>
              <ul className="space-y-3">
                {col.points.map((p, j) => (
                  <li key={j} className={`flex items-start gap-2 text-sm ${col.highlight ? 'text-white/90' : 'text-ink-muted'}`}>
                    <span className={`mt-0.5 shrink-0 font-bold ${col.highlight ? 'text-white' : 'text-ink-muted'}`}>{col.icon}</span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>


      {/* DEMO CALLOUT — "Should I take the new job?" */}
      <section className="anim-rise relative" style={{ animationDelay: '240ms' }} data-testid="demo-callout">
        <Link
          to="/app?seed=job-change"
          data-testid="demo-callout-cta"
          className="block card-brutal card-brutal-hover p-6 sm:p-8 bg-gradient-to-br from-secondary/15 via-surface to-accent/15 group"
        >
          <div className="grid sm:grid-cols-[auto_1fr_auto] items-center gap-5">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl border-2 border-edge bg-secondary text-white grid place-items-center shadow-brutal-sm shrink-0 transform group-hover:-rotate-6 transition-transform">
              <Briefcase size={30} weight="fill" />
            </div>
            <div className="min-w-0">
              <div className="chip border-secondary text-secondary-ink inline-flex mb-2">
                <Sparkle size={12} weight="fill" className="mr-1" /> Pre-loaded example
              </div>
              <h3 className="font-heading text-xl sm:text-2xl font-black tracking-tight leading-tight">
                Should I take the new job?{' '}
                <span className="text-secondary-ink">Try a pre-loaded example →</span>
              </h3>
              <p className="text-sm text-ink-muted mt-1">
                Pre-filled with salary, annual raise, layoff risk, and job satisfaction — both options ready. Tweak the numbers to match your situation, hit Run.
              </p>
            </div>
            <div
              className="hidden sm:flex flex-col items-end shrink-0 text-right font-heading"
              aria-hidden="true"
            >
              <span className="text-xs font-bold uppercase tracking-wider text-ink-muted">1,000</span>
              <span className="font-black text-lg text-secondary-ink mono">futures/run</span>
            </div>
          </div>
        </Link>
        <p className="text-sm text-ink-muted mt-3 text-center">
          Just weighing a job change?{' '}
          <Link to="/should-i-quit" data-testid="forerunner-link" className="font-bold text-primary-ink underline underline-offset-2 hover:no-underline">
            Try the free 60-second version →
          </Link>{' '}
          No signup, no numbers to model.
        </p>
      </section>

      {/* TESTIMONIALS — hidden until we have real, attributable quotes */}
      {SHOW_TESTIMONIALS && (
      <section className="grid md:grid-cols-3 gap-6" style={{ animationDelay: '300ms' }}>
        {TESTIMONIALS.map((t, i) => (
          <div key={i} className="anim-rise card-brutal p-6" style={{ animationDelay: `${300 + i * 60}ms` }}>
            <div className="font-heading text-2xl mb-2">"</div>
            <p className="leading-relaxed mb-4">{t.quote}</p>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full border-2 border-edge grid place-items-center text-xs font-black ${t.bg}`}>
                {t.initials}
              </div>
              <div>
                <div className="font-bold text-sm">{t.name}</div>
                <div className="text-xs text-ink-muted">{t.role}</div>
              </div>
            </div>
          </div>
        ))}
      </section>
      )}

      {/* Blog teaser */}
      <BlogTeaser />

      {/* FAQ */}
      <section className="anim-rise" style={{ animationDelay: '360ms' }}>
        <div className="chip border-secondary text-secondary-ink inline-flex mb-3">FAQ</div>
        <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-6">Things we get asked</h2>
        <div className="space-y-3">
          {FAQ.map((f, i) => (
            <details key={i} className="card-brutal p-5 group" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer font-heading text-lg font-bold flex items-center justify-between list-none">
                {f.q}
                <span className="font-mono text-primary-ink group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-ink-muted mt-3">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="anim-rise card-brutal p-8 sm:p-12 text-center bg-gradient-to-br from-primary/15 to-secondary/15 relative overflow-hidden" style={{ animationDelay: '420ms' }} data-testid="bottom-cta">
        <Confetti size={120} weight="duotone" className="absolute right-4 top-4 opacity-20 text-primary-ink animate-float" />
        <h2 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter mb-3">
          Run your first simulation <span className="text-primary-ink">in 3 minutes.</span>
        </h2>
        <p className="text-ink-muted max-w-xl mx-auto mb-5">
          Free forever. No credit card. No spreadsheet.
        </p>
        <div className="flex gap-2 justify-center flex-wrap">
          <Link to={ctaTarget} data-testid="cta-final" className="btn-primary text-lg px-8 py-4 inline-flex items-center gap-2">
            {ctaLabel} <ArrowRight size={20} weight="bold" />
          </Link>
          {!user && (
            <Link to="/signup" className="btn-secondary text-lg px-8 py-4" data-testid="cta-final-signup">
              Create free account
            </Link>
          )}
        </div>
      </section>
    </div>
    </>
  )
}

const FEATURES = [
  {
    Icon: ChartLine, bg: 'bg-primary/15', color: 'text-primary-ink',
    title: 'Monte Carlo, not magic',
    body: '1,000–10,000 simulated futures per decision. NPV histograms, CDFs, cashflow lines, all bento-laid-out.',
  },
  {
    Icon: Robot, bg: 'bg-secondary/15', color: 'text-secondary-ink',
    title: 'AI Decision Coach',
    body: 'Asks the questions you\'re avoiding. Then gives you a verdict — not cheerleading.',
  },
  {
    Icon: Sparkle, bg: 'bg-accent/30', color: 'text-ink',
    title: 'AI-suggested variables',
    body: 'Stuck modelling the messy details? One click and Claude proposes income, costs, and risks with realistic priors.',
  },
  {
    Icon: Lightning, bg: 'bg-yellow-400/30', color: 'text-ink',
    title: 'Tornado sensitivity',
    body: 'Which variable could flip the result? We perturb each ±20% and rank by impact, instantly.',
  },
  {
    Icon: ShieldCheck, bg: 'bg-secondary/15', color: 'text-secondary-ink',
    title: 'Bayesian updating',
    body: "When reality lands, log the actual number. Your priors update automatically — the model gets smarter as you go.",
  },
  {
    Icon: Question, bg: 'bg-primary/15', color: 'text-primary-ink',
    title: 'Shareable results',
    body: 'One-click public link — or fork someone else\'s decision and run it with your numbers. Send your verdict to a friend, partner, or smug uncle who insisted you take the Master\'s.',
  },
]

const STEPS = [
  { title: 'Describe your options', body: '"Stay" vs "Take new job" vs "Start freelancing." Add as many as you want.' },
  { title: 'Model the uncertainty', body: 'Salary, costs, risks — each as a range, not a single number. Or let the AI suggest them.' },
  { title: 'See the winning future', body: '1,000+ alt-futures, winner banner, NPV charts, sensitivity, and an AI-written verdict.' },
]

const SHOW_TESTIMONIALS = false

const TESTIMONIALS = [
  {
    quote: 'The Decision Agent absolutely roasted my "Hell yes!" answer for a second Master\'s. Saved me €90K and two years of regret.',
    name: 'Melina S.', role: 'Project manager · chose against a second Master\'s', initials: 'MS', bg: 'bg-accent',
  },
  {
    quote: 'I always thought "take the higher-paying job." LifeOdds showed me the layoff risk dominated NPV. I stayed and got promoted 6 months later.',
    name: 'Maya R.', role: 'Senior engineer · stayed, promoted 6 months later', initials: 'MR', bg: 'bg-primary text-white',
  },
  {
    quote: "I ran it on whether to raise a seed round or stay bootstrapped. The model said bootstrap — and showed me exactly which variable (runway) would flip the answer. We stayed bootstrapped. Six months later: profitable.",
    name: 'Theo K.', role: 'Founder · stayed bootstrapped, hit profitability', initials: 'TK', bg: 'bg-secondary text-white',
  },
]

// Live count, never typed in (profile-concept audit C1: the old hard-coded
// "3,000+" couldn't be checked). Rounded down so it never overstates, and
// hidden below USAGE_FLOOR or on any error: no number beats a thin one.
const USAGE_FLOOR = 100

function roundUsageDown(n: number): number {
  const step = n >= 10000 ? 1000 : n >= 1000 ? 100 : 10
  return Math.floor(n / step) * step
}

function UsageCount() {
  const [n, setN] = useState<number | null>(null)
  useEffect(() => {
    getPublicUsage().then((r) => setN(r.decisions_last_7_days)).catch(() => {})
  }, [])
  if (n === null || n < USAGE_FLOOR) return null
  return (
    <div className="text-center text-sm font-bold mb-3" data-testid="usage-count">
      <span className="mono">{roundUsageDown(n).toLocaleString('en-US')}+</span> decisions run in the last 7 days
    </div>
  )
}

function BlogTeaser() {
  const [posts, setPosts] = useState<GhostPost[]>([])
  useEffect(() => {
    getPosts(3).then((r) => setPosts(r.posts)).catch(() => {})
  }, [])
  if (posts.length === 0) return null
  return (
    <section className="anim-rise space-y-6" style={{ animationDelay: '330ms' }}>
      <div className="flex items-center justify-between">
        <div>
          <div className="chip border-primary text-primary-ink inline-flex mb-2">From the blog</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Real decisions. Real numbers.</h2>
        </div>
        <Link to="/blog" className="hidden sm:inline-flex items-center gap-1 text-sm font-bold text-primary-ink hover:underline">
          All posts <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {posts.map((post, i) => (
          <Link
            key={post.id}
            to={`/blog/${post.slug}`}
            className="card-brutal card-brutal-hover p-5 flex flex-col gap-3 group"
            style={{ animationDelay: `${i * 50}ms` }}
          >
            {post.feature_image && (
              <div className="rounded-lg overflow-hidden border-2 border-edge aspect-video">
                <img src={post.feature_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              </div>
            )}
            <h3 className="font-heading font-bold text-base leading-tight group-hover:text-primary-ink transition-colors line-clamp-2">{post.title}</h3>
            {post.excerpt && <p className="text-xs text-ink-muted line-clamp-2 leading-relaxed">{post.excerpt}</p>}
            <span className="text-xs text-ink-muted mt-auto">{formatDate(post.published_at)}</span>
          </Link>
        ))}
      </div>
      <Link to="/blog" className="sm:hidden inline-flex items-center gap-1 text-sm font-bold text-primary-ink hover:underline">
        All posts <ArrowRight size={14} weight="bold" />
      </Link>
    </section>
  )
}

const COMPARISON = [
  {
    label: 'Spreadsheet',
    icon: '✗',
    highlight: false,
    points: [
      'One number per assumption — no uncertainty',
      'You build it from scratch every time',
      'No sensitivity ranking — you guess what matters',
      'Breaks the moment your assumptions change',
    ],
  },
  {
    label: 'LifeOdds',
    icon: '✓',
    highlight: true,
    points: [
      'Ranges, not single numbers — models real uncertainty',
      'AI suggests variables with realistic priors',
      'Tornado chart shows exactly what could flip the result',
      'Update one number, re-run in seconds',
    ],
  },
  {
    label: 'Gut feel',
    icon: '✗',
    highlight: false,
    points: [
      'Anchors on whatever you thought of first',
      'Ignores compounding over a 5–10 year horizon',
      'Overweights recent events and vivid scenarios',
      'Can\'t tell you which variable you\'re most wrong about',
    ],
  },
]

const FAQ = [
  { q: 'Is LifeOdds free?', a: 'Yes, free forever for personal use. No credit card. You can use templates without an account; AI features require a free signup.' },
  { q: 'Is the upgrade a subscription?', a: 'No. The upgrade is a one-time payment — you pay once and own it forever. No renewal, no monthly charge.' },
  { q: 'Do you store my financial data?', a: 'Decisions you build (without an account) stay in your browser localStorage. With an account, simulation results are stored in our database against your account.' },
  { q: 'Which AI model do you use?', a: 'We use Claude Fable by Anthropic — the best available model for structured reasoning on nuanced trade-offs. No API key needed from you.' },
  { q: 'Can I use this for business decisions?', a: 'Absolutely. The same engine works for hiring, pricing, market-entry, and capex decisions. A team tier is on the roadmap.' },
  { q: 'How accurate is Monte Carlo for life decisions?', a: 'It won\'t tell you what will happen. It will tell you which decision wins more futures, and which variable could flip the answer. That\'s the question worth asking.' },
]

// FAQPage structured data — mirrors the visible FAQ above so answer engines can
// surface it. The SSR homepage (marketing_ssr._faq_schema) emits the same for
// no-JS crawlers.
const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
}
